import { ColumnsType } from "antd/es/table";
import { IPayersGet } from "../../../../../../../../../types/Payer";
import dayjs from "../../../../../../../../../configs/dayjs";
import {
  DATE_FORMAT,
  PAYER_STATUS,
} from "../../../../../../../../../lib/constants";
import { Link } from "react-router-dom";
import { Row, Tag } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useButtonHandlers } from "./useButtonHandlers";
import { useCallback, useMemo } from "react";
import { getFormattedPercentage } from "../../../../../../../../../lib/percentageFormatter";
import ROUTES from "../../../../../../../../../configs/routesConfig";
import { usePermissions } from "../../../../../../../../../lib/hooks/usePermissions";
import { ReactComponent as IconPersonPrimary } from "../../../../../../../../../images/icons/PersonPrimary.svg";
import { ReactComponent as IconPersonSecondary } from "../../../../../../../../../images/icons/PersonSecondary.svg";

type TTableColumns = {
  fetchData: () => void;
  setId: React.Dispatch<React.SetStateAction<string | null | undefined>>;
};

export const useGetTableColumns = ({ fetchData, setId }: TTableColumns) => {
  const { hasAccessByController } = usePermissions("ContractPayers");

  const { onDeleteBtnClick } = useButtonHandlers({
    fetchData,
    setId,
  });

  const handleDeleteBtnClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      onDeleteBtnClick(e.currentTarget.id);
    },
    [onDeleteBtnClick],
  );

  const handleEditBtnClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      setId(e.currentTarget.id);
    },
    [setId],
  );

  const columns: ColumnsType<IPayersGet> = useMemo(() => {
    const values: ColumnsType<IPayersGet> = [
      {
        title: "Payer",
        key: "payer",
        dataIndex: "payerId",
        width: "20%",
        ellipsis: true,
        render: (value: string, record: IPayersGet) => {
          const { payerName, type } = record;

          return (
            <Row className="flex-column elipsis w-100">
              <Link
                className="link-text-color w-100 d-flex align-center"
                to={`${ROUTES.CUSTOMERS}/${value}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="mt-1">
                  {type === "Primary Payer" ? (
                    <IconPersonPrimary />
                  ) : (
                    <IconPersonSecondary />
                  )}
                </span>
                <span className="ml-3 ant-btn-link elipsis">{payerName}</span>
              </Link>
            </Row>
          );
        },
      },
      {
        title: "Percentage",
        key: "percentage",
        dataIndex: "percentage",
        width: "11%",
        align: "right",
        render: (value: number) => getFormattedPercentage(value),
      },
      {
        title: "Type",
        width: "31%",
        dataIndex: "type",
        key: "type",
        ellipsis: true,
      },
      {
        title: "Assumed Date",
        key: "assumedDate",
        dataIndex: "assumedDate",
        width: "10%",
        render: (value: string) =>
          value ? dayjs(value).format(DATE_FORMAT) : "",
      },
      {
        title: "Sold Date",
        key: "soldDate",
        dataIndex: "soldDate",
        width: "10%",
        render: (value: string) =>
          value ? dayjs(value).format(DATE_FORMAT) : "",
      },
      {
        title: "Status",
        key: "status",
        dataIndex: "status",
        width: "8%",
        render: (value: number) => {
          const color: string = value === 0 ? "success" : "";

          const text = PAYER_STATUS.find(
            (status) => status.value === value,
          )?.label;

          return <Tag color={color}>{text}</Tag>;
        },
      },
    ];

    const menuKeys: string[] = [];

    hasAccessByController("Modify") && menuKeys.push("1");

    hasAccessByController("Delete") && menuKeys.push("2");

    menuKeys.length > 0 &&
      values.push({
        key: "actions",
        width: "10%",
        render: (_: any, record: IPayersGet) => {
          const { id } = record;

          return (
            <Row className="ant-table-actions justify-end">
              {menuKeys.includes("1") && (
                <EditOutlined
                  className="ant-table-btn"
                  id={id}
                  onClick={handleEditBtnClick}
                />
              )}
              {menuKeys.includes("2") && (
                <DeleteOutlined
                  id={id}
                  className="ant-table-btn"
                  onClick={handleDeleteBtnClick}
                />
              )}
            </Row>
          );
        },
      });

    return values;
  }, [hasAccessByController, handleDeleteBtnClick, handleEditBtnClick]);

  return { columns };
};
